/**
 * TOUR-02 — Parsing du fichier normalisé de mise à jour du plan de tournée.
 *
 * Format attendu : `Nomenclature;Adresse[;Latitude;Longitude]`, séparateur `;`,
 * fins de ligne `\n` ou `\r\n`, guillemets RFC 4180 (une cellule contenant `;`, `"` ou un
 * retour à la ligne est entourée de `"` — c'est ce qu'Excel produit quand une cellule
 * contient un saut de ligne). Fonction pure, sans dépendance : testable seule.
 *
 * Spec : les-eugene-docs/features/TOUR-02-maj-plan-tournee-csv/cadrage-maj-plan-tournee-csv.md
 * (règles R1, R2, R4, R5, R6, R7).
 *
 * @typedef {Object} TourneeCsvRow
 * @property {number} line          n° d'enregistrement = **ligne Excel** (1-based, en-tête = 1) ;
 *                                  une cellule multi-lignes ne compte qu'une fois
 * @property {string} nomenclature  brute, **jamais trimée** (R3)
 * @property {string} adresse       trimée, "" si absente
 * @property {string|null} latitude  normalisée (point décimal) ou null si non fournie
 * @property {string|null} longitude idem
 * @property {number} ordreCsv      rang parmi les lignes de données non vides (R7)
 *
 * @typedef {Object} TourneeCsvIssue
 * @property {string} code
 * @property {string} message
 * @property {number[]} lines
 *
 * @typedef {Object} TourneeCsvParseResult
 * @property {TourneeCsvRow[]} rows
 * @property {TourneeCsvIssue[]} errors    bloquants (R15)
 * @property {TourneeCsvIssue[]} warnings  informatifs
 * @property {boolean} hasCoords           colonnes Latitude/Longitude présentes
 */

export const CSV_SEPARATOR = ';';

const COL_NOMENCLATURE = 'nomenclature';
const COL_ADRESSE = 'adresse';
const COL_LATITUDE = 'latitude';
const COL_LONGITUDE = 'longitude';

const REQUIRED_COLUMNS = [COL_NOMENCLATURE, COL_ADRESSE];
const COORD_COLUMNS = [COL_LATITUDE, COL_LONGITUDE];
const KNOWN_COLUMNS = [...REQUIRED_COLUMNS, ...COORD_COLUMNS];

const DECIMAL_NUMBER = /^-?\d+(\.\d+)?$/;

/** Normalise un en-tête : trim, minuscules, sans accents. */
export function normalizeHeader(header) {
    return String(header ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Parse une coordonnée : virgule décimale acceptée (R6).
 * @returns {{ ok: true, value: string, num: number } | { ok: false, reason: 'invalid' | 'range' }}
 */
export function parseCoordinate(raw, kind) {
    const normalized = String(raw ?? '').trim().replace(',', '.');
    if (!DECIMAL_NUMBER.test(normalized)) {
        return {ok: false, reason: 'invalid'};
    }
    const num = Number(normalized);
    const max = kind === 'latitude' ? 90 : 180;
    if (!Number.isFinite(num) || num < -max || num > max) {
        return {ok: false, reason: 'range'};
    }
    return {ok: true, value: normalized, num};
}

function issue(code, message, lines) {
    return {code, message, lines: [...lines].sort((a, b) => a - b)};
}

/**
 * Découpe un texte CSV en enregistrements (RFC 4180) : séparateur `;`, guillemets doubles,
 * `""` = guillemet littéral, retours à la ligne autorisés dans une cellule entre guillemets.
 * Un enregistrement = une ligne Excel, même s'il s'étale sur plusieurs lignes physiques.
 * @param {string} text
 * @returns {string[][]} enregistrements (cellules brutes, sans guillemets d'encadrement)
 */
export function splitCsvRecords(text) {
    const records = [];
    let record = [];
    let cell = '';
    let inQuotes = false;
    let cellStarted = false;
    const src = String(text ?? '');

    for (let i = 0; i < src.length; i++) {
        const ch = src[i];
        if (inQuotes) {
            if (ch === '"') {
                if (src[i + 1] === '"') { cell += '"'; i++; } // "" → "
                else inQuotes = false;
            } else {
                cell += ch;
            }
            continue;
        }
        if (ch === '"' && cell.trim() === '') {
            // guillemet ouvrant (tolère des espaces avant)
            inQuotes = true;
            cell = '';
            cellStarted = true;
        } else if (ch === CSV_SEPARATOR) {
            record.push(cell);
            cell = '';
            cellStarted = true;
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && src[i + 1] === '\n') i++;
            record.push(cell);
            records.push(record);
            record = [];
            cell = '';
            cellStarted = false;
        } else {
            cell += ch;
            cellStarted = true;
        }
    }
    if (cellStarted || record.length > 0) {
        record.push(cell);
        records.push(record);
    }
    return records;
}

/**
 * @param {string} text contenu déjà décodé du fichier
 * @returns {TourneeCsvParseResult}
 */
export function parseTourneeCsv(text) {
    const errors = [];
    const warnings = [];
    const rows = [];

    const records = splitCsvRecords(text);
    const isBlank = (rec) => rec.every((c) => c.trim() === '');
    if (records.length === 0 || isBlank(records[0])) {
        errors.push(issue('EMPTY_FILE', 'Le fichier est vide (aucune ligne d\'en-tête).', [1]));
        return {rows, errors, warnings, hasCoords: false};
    }

    // --- En-têtes (R1) ---------------------------------------------------------
    const rawHeaders = records[0];
    const headers = rawHeaders.map(normalizeHeader);
    const columnIndex = {};
    headers.forEach((h, i) => {
        if (h === '') return;
        if (columnIndex[h] !== undefined) {
            errors.push(issue('DUPLICATE_HEADER', `Colonne « ${rawHeaders[i].trim()} » présente deux fois.`, [1]));
            return;
        }
        columnIndex[h] = i;
    });

    REQUIRED_COLUMNS.forEach((col) => {
        if (columnIndex[col] === undefined) {
            errors.push(issue('MISSING_HEADER', `Colonne « ${capitalize(col)} » manquante.`, [1]));
        }
    });

    const hasLat = columnIndex[COL_LATITUDE] !== undefined;
    const hasLng = columnIndex[COL_LONGITUDE] !== undefined;
    if (hasLat !== hasLng) {
        const present = hasLat ? 'Latitude' : 'Longitude';
        const missing = hasLat ? 'Longitude' : 'Latitude';
        errors.push(issue('MISSING_HEADER', `Colonne « ${missing} » manquante (« ${present} » présente).`, [1]));
    }
    const hasCoords = hasLat && hasLng;

    const extra = Object.keys(columnIndex).filter((h) => !KNOWN_COLUMNS.includes(h));
    if (extra.length > 0) {
        const labels = extra.map((h) => rawHeaders[columnIndex[h]].trim());
        warnings.push(issue('IGNORED_COLUMNS', `Colonne(s) ignorée(s) : ${labels.join(', ')}.`, [1]));
    }

    if (errors.length > 0) {
        // Sans en-têtes valides, le reste n'a pas de sens.
        return {rows, errors, warnings, hasCoords};
    }

    // --- Lignes de données (R2, R6, R7) -------------------------------------------
    let ordre = 0;
    for (let i = 1; i < records.length; i++) {
        const cells = records[i];
        if (isBlank(cells)) continue; // R2 : lignes vides ignorées, numérotation conservée
        const lineNumber = i + 1; // = ligne Excel
        ordre += 1;

        const cell = (col) => (columnIndex[col] !== undefined ? (cells[columnIndex[col]] ?? '') : '');

        // R3 : brute (sans trim) — seuls les retours à la ligne parasites d'Excel sont retirés
        const nomenclature = cell(COL_NOMENCLATURE).replace(/[\r\n]/g, '');
        if (nomenclature === '') {
            errors.push(issue('EMPTY_NOMENCLATURE', `Ligne ${lineNumber} : nomenclature vide.`, [lineNumber]));
        }

        const adresse = cell(COL_ADRESSE).trim();

        let latitude = null;
        let longitude = null;
        if (hasCoords) {
            const rawLat = cell(COL_LATITUDE).trim();
            const rawLng = cell(COL_LONGITUDE).trim();
            if (rawLat === '' && rawLng === '') {
                // non fournies pour cette ligne
            } else if (rawLat === '' || rawLng === '') {
                const missing = rawLat === '' ? 'latitude' : 'longitude';
                errors.push(issue('MISSING_COORD', `Ligne ${lineNumber} : ${missing} manquante.`, [lineNumber]));
            } else {
                const lat = parseCoordinate(rawLat, 'latitude');
                const lng = parseCoordinate(rawLng, 'longitude');
                if (!lat.ok) {
                    errors.push(issue('INVALID_COORD', `Ligne ${lineNumber} : latitude ${lat.reason === 'range' ? 'hors bornes' : 'invalide'} (« ${rawLat} »).`, [lineNumber]));
                }
                if (!lng.ok) {
                    errors.push(issue('INVALID_COORD', `Ligne ${lineNumber} : longitude ${lng.reason === 'range' ? 'hors bornes' : 'invalide'} (« ${rawLng} »).`, [lineNumber]));
                }
                if (lat.ok && lng.ok) {
                    latitude = lat.value;
                    longitude = lng.value;
                }
            }
        }

        rows.push({line: lineNumber, nomenclature, adresse, latitude, longitude, ordreCsv: ordre});
    }

    if (rows.length === 0) {
        warnings.push(issue('NO_DATA', 'Le fichier ne contient aucune ligne de données.', [1]));
    }

    // --- Doublons (R4, R5) -----------------------------------------------------------
    const byNomenclature = new Map();
    rows.forEach((r) => {
        if (r.nomenclature === '') return;
        if (!byNomenclature.has(r.nomenclature)) byNomenclature.set(r.nomenclature, []);
        byNomenclature.get(r.nomenclature).push(r.line);
    });
    byNomenclature.forEach((lineNumbers, nomenclature) => {
        if (lineNumbers.length > 1) {
            errors.push(issue('DUPLICATE_NOMENCLATURE', `Nomenclature ${nomenclature} en double (lignes ${lineNumbers.join(', ')}).`, lineNumbers));
        }
    });

    const byCoords = new Map();
    rows.forEach((r) => {
        if (r.latitude === null || r.longitude === null) return;
        const key = `${Number(r.latitude)}|${Number(r.longitude)}`;
        if (!byCoords.has(key)) byCoords.set(key, []);
        byCoords.get(key).push(r.line);
    });
    byCoords.forEach((lineNumbers, key) => {
        if (lineNumbers.length > 1) {
            const [lat, lng] = key.split('|');
            errors.push(issue('DUPLICATE_COORDS', `Coordonnées identiques lignes ${lineNumbers.join(' et ')} (${lat}, ${lng}).`, lineNumbers));
        }
    });

    errors.sort((a, b) => a.lines[0] - b.lines[0]);

    return {rows, errors, warnings, hasCoords};
}

function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export default parseTourneeCsv;
